/**
 * BuzzBit X Design Tokens -- BuzzBit X Design System (Klaviyo-inspired, Manus-AI flat)
 *
 * Grayscale-first (zinc), near-black CTAs, pastel-only status pairs.
 * Source of truth: the BuzzBit X Design System `colors_and_type.css` (vars --bx-*).
 * No violet/purple anywhere. Inter for type, JetBrains Mono for code/IDs.
 */

// ===== NEW NAMED EXPORTS (BuzzBit X Design System palette) =====

export const colors = {
  // Backgrounds
  background: {
    primary: '#FFFFFF',
    secondary: '#FAFAFA',
    tertiary: '#F4F4F5',
    hover: '#F4F4F5',
    active: '#E4E4E7',
    inverse: '#18181B',
  },

  // Text
  text: {
    primary: '#18181B',
    secondary: '#71717A',
    tertiary: '#A1A1AA',
    disabled: '#D4D4D8',
    inverse: '#FFFFFF',
    link: '#18181B',
  },

  // Borders
  border: {
    default: '#E4E4E7',
    strong: '#D4D4D8',
    subtle: '#F4F4F5',
    focus: '#18181B',
  },

  // Accent (near-black -- used for buttons, active nav, focus rings)
  accent: {
    DEFAULT: '#18181B',
    hover: '#27272A',
    active: '#09090B',
    subtle: '#F4F4F5',
    muted: '#3F3F46',
  },

  // State colors -- pastel bg + dark fg pairs (AA-compliant per DS)
  state: {
    success: { bg: '#ECFDF5', border: '#A7F3D0', text: '#047857', icon: '#047857' },
    error: { bg: '#FEF2F2', border: '#FECACA', text: '#B91C1C', icon: '#B91C1C' },
    warning: { bg: '#FFFBEB', border: '#FDE68A', text: '#B45309', icon: '#B45309' },
    info: { bg: '#EFF6FF', border: '#BFDBFE', text: '#1D4ED8', icon: '#1D4ED8' },
  },

  // Gray scale (zinc, 8 stops)
  gray: {
    50: '#FAFAFA',
    100: '#F4F4F5',
    200: '#E4E4E7',
    300: '#D4D4D8',
    400: '#A1A1AA',
    500: '#71717A',
    600: '#52525B',
    700: '#3F3F46',
    800: '#27272A',
    900: '#18181B',
  },
};

export const typography = {
  fontFamily: {
    sans: 'Onest, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
    mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
  },
  fontSize: {
    xs: '0.75rem',    // 12px
    sm: '0.8125rem',  // 13px
    base: '0.875rem', // 14px
    md: '1rem',       // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
  },
  fontWeight: {
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '600', // DS allows 400/500/600 only -- bold aliased to semibold
  },
  lineHeight: {
    tight: '1.25',
    normal: '1.5',
    relaxed: '1.75',
  },
};

export const spacing = {
  0: '0',
  1: '0.25rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  8: '2rem',
  10: '2.5rem',
  12: '3rem',
  16: '4rem',
  20: '5rem',
};

export const borderRadius = {
  none: '0',
  sm: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  full: '9999px',
};

export const shadows = {
  none: 'none',
  xs: '0 1px 0 rgba(24, 24, 27, 0.04)',
  sm: '0 1px 2px rgba(24, 24, 27, 0.05), 0 1px 1px rgba(24, 24, 27, 0.04)',
  md: '0 2px 4px rgba(24, 24, 27, 0.06), 0 1px 2px rgba(24, 24, 27, 0.04)',
  lg: '0 4px 8px rgba(24, 24, 27, 0.06), 0 2px 4px rgba(24, 24, 27, 0.04)',
  focus: '0 0 0 3px rgba(24, 24, 27, 0.08)',
};

export const transitions = {
  fast: '120ms cubic-bezier(0.2, 0, 0, 1)',
  normal: '180ms cubic-bezier(0.2, 0, 0, 1)',
  slow: '280ms cubic-bezier(0.2, 0, 0, 1)',
};

// ===== BACKWARD-COMPATIBLE `tokens` EXPORT =====
// All existing components import `tokens` and reference properties like
// tokens.colors.accent[600], tokens.colors.gray[200], tokens.typography.fontFamily, etc.
// This object preserves that exact shape but maps to the BuzzBit X Design System palette.

export const tokens = {
  colors: {
    white: '#FFFFFF',
    black: '#000000',

    // Neutrals (Gray scale) -- zinc
    gray: {
      50: '#FAFAFA',
      100: '#F4F4F5',
      200: '#E4E4E7',
      300: '#D4D4D8',
      400: '#A1A1AA',
      500: '#71717A',
      600: '#52525B',
      700: '#3F3F46',
      800: '#27272A',
      900: '#18181B',
    },

    // Accent (near-black -- NO violet; tints are zinc)
    accent: {
      50: '#FAFAFA',
      100: '#F4F4F5',
      200: '#E4E4E7',
      300: '#D4D4D8',
      400: '#A1A1AA',
      500: '#3F3F46',
      600: '#18181B',   // Primary accent
      700: '#27272A',   // Hover
      800: '#09090B',   // Active/pressed
      900: '#09090B',   // Darkest
    },

    // Semantic colors -- DS pastel pairs (light bg / dark AA fg)
    success: {
      light: '#ECFDF5',
      main: '#047857',
      dark: '#065F46',
    },
    warning: {
      light: '#FFFBEB',
      main: '#B45309',
      dark: '#92400E',
    },
    error: {
      light: '#FEF2F2',
      main: '#B91C1C',
      dark: '#991B1B',
    },
    info: {
      light: '#EFF6FF',
      main: '#1D4ED8',
      dark: '#1E3A8A',
    },
  },

  // Manus Colors (kept for backward compat, identical to zinc gray scale)
  manusColors: {
    white: '#FFFFFF',
    gray: {
      50: '#FAFAFA',
      100: '#F4F4F5',
      200: '#E4E4E7',
      300: '#D4D4D8',
      400: '#A1A1AA',
      500: '#71717A',
      600: '#52525B',
      700: '#3F3F46',
      800: '#27272A',
      900: '#18181B',
    },
  },

  // Typography
  typography: {
    fontFamily: 'Onest, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',

    fontSize: {
      xs: '0.75rem',     // 12px
      sm: '0.8125rem',   // 13px
      base: '0.875rem',  // 14px
      lg: '1.125rem',    // 18px
      xl: '1.25rem',     // 20px
      '2xl': '1.5rem',   // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem',  // 36px
    },

    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 600, // DS allows 400/500/600 only -- bold aliased to semibold
    },

    lineHeight: {
      tight: 1.25,
      snug: 1.375,
      normal: 1.5,
      relaxed: 1.625,
    },

    letterSpacing: {
      tight: '-0.015em',
      normal: '0',
      wide: '0.025em',
    },
  },

  // Spacing (4px base unit)
  spacing: {
    0: '0',
    1: '0.25rem',
    2: '0.5rem',
    3: '0.75rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    8: '2rem',
    10: '2.5rem',
    12: '3rem',
    16: '4rem',
    20: '5rem',
    24: '6rem',
  },

  // Border Radius
  borderRadius: {
    none: '0',
    sm: '4px',
    md: '6px',
    lg: '8px',
    xl: '12px',
    '2xl': '16px',
    '3xl': '24px',
    full: '9999px',
  },

  // Shadows -- flat-ish (DS caps elevation at a 4-8px blur)
  boxShadow: {
    none: 'none',
    xs: '0 1px 0 rgba(24, 24, 27, 0.04)',
    sm: '0 1px 2px rgba(24, 24, 27, 0.05), 0 1px 1px rgba(24, 24, 27, 0.04)',
    md: '0 2px 4px rgba(24, 24, 27, 0.06), 0 1px 2px rgba(24, 24, 27, 0.04)',
    lg: '0 4px 8px rgba(24, 24, 27, 0.06), 0 2px 4px rgba(24, 24, 27, 0.04)',
    xl: '0 4px 8px rgba(24, 24, 27, 0.06), 0 2px 4px rgba(24, 24, 27, 0.04)',   // aliased to lg ceiling
    '2xl': '0 4px 8px rgba(24, 24, 27, 0.06), 0 2px 4px rgba(24, 24, 27, 0.04)', // aliased to lg ceiling
    inner: '0 4px 8px rgba(24, 24, 27, 0.06), 0 2px 4px rgba(24, 24, 27, 0.04)', // aliased to lg ceiling
  },

  // Manus Shadows (Flow Builder -- flattened per DS)
  manusShadows: {
    node: {
      default: '0 1px 0 rgba(24, 24, 27, 0.04)',
      hover: '0 1px 2px rgba(24, 24, 27, 0.05), 0 1px 1px rgba(24, 24, 27, 0.04)',
      selected: '0 0 0 2px rgba(24, 24, 27, 0.08)',
    },
    ui: {
      card: '0 1px 0 rgba(24, 24, 27, 0.04)',
      dropdown: '0 2px 4px rgba(24, 24, 27, 0.06), 0 1px 2px rgba(24, 24, 27, 0.04)',
      modal: '0 4px 8px rgba(24, 24, 27, 0.06), 0 2px 4px rgba(24, 24, 27, 0.04)',
    },
  },

  // Borders
  borderWidth: {
    0: '0',
    1: '1px',
    2: '2px',
    4: '4px',
    8: '8px',
  },

  // Z-Index Scale
  zIndex: {
    hide: -1,
    auto: 'auto',
    base: 0,
    dropdown: 1000,
    sticky: 1100,
    fixed: 1200,
    modal: 1300,
    popover: 1400,
    tooltip: 1500,
  },

  // Breakpoints
  breakpoints: {
    mobile: '0px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1280px',
  },

  // Transitions
  transition: {
    fast: '120ms cubic-bezier(0.2, 0, 0, 1)',
    base: '180ms cubic-bezier(0.2, 0, 0, 1)',
    slow: '280ms cubic-bezier(0.2, 0, 0, 1)',
    slowest: '400ms cubic-bezier(0.2, 0, 0, 1)',
  },

  // Opacity
  opacity: {
    0: '0',
    10: '0.1',
    20: '0.2',
    30: '0.3',
    40: '0.4',
    50: '0.5',
    60: '0.6',
    70: '0.7',
    80: '0.8',
    90: '0.9',
    100: '1',
  },
};

// Type exports for TypeScript autocomplete
export type ColorScale = keyof typeof tokens.colors.gray;
export type AccentShade = keyof typeof tokens.colors.accent;
export type Spacing = keyof typeof tokens.spacing;
export type BorderRadius = keyof typeof tokens.borderRadius;
export type BoxShadow = keyof typeof tokens.boxShadow;
export type FontSize = keyof typeof tokens.typography.fontSize;
export type FontWeight = keyof typeof tokens.typography.fontWeight;
export type Breakpoint = keyof typeof tokens.breakpoints;
export type ManusColorScale = keyof typeof tokens.manusColors.gray;
export type ManusNodeShadow = keyof typeof tokens.manusShadows.node;
export type ManusUIShadow = keyof typeof tokens.manusShadows.ui;

// Default export for backward compatibility
export default { colors, typography, spacing, borderRadius, shadows, transitions };
